import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Spin, Button } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import ViewRewardModal from '../../ViewRewardModal/ViewRewardModal.jsx';
import CoreBadge from 'src/RnRCommonComponent/CoreBadge/CoreBadge';

const ModernBadgeGrid = (props) => {
	const {
		handleIconClickEvent,
		title,
		titleLink,
		titleLinkText,
		badgeList,
		hasMore,
		isLoading,
		fetchMoreData,
		isDetailsShown,
		handleOpenViewReward,
		handleCloseViewReward,
		isViewRewardModalVisible,
		rewardDetails,
		isModalLoaderVisible,
		getMyBadge,
		currentUser,
		customHeight,
		noCollapse = false,
	} = props;

	const containerRef = useRef(null);
	const [isExpanded, setIsExpanded] = useState(noCollapse);

	const handleScroll = useCallback(() => {
		if (!containerRef.current || !hasMore || isLoading) return;

		const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
		const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

		if (scrollPercentage > 0.85) {
			fetchMoreData();
		}
	}, [hasMore, isLoading, fetchMoreData]);

	useEffect(() => {
		const container = containerRef.current;
		if (container && hasMore) {
			container.addEventListener('scroll', handleScroll);
			return () => container.removeEventListener('scroll', handleScroll);
		}
	}, [handleScroll, hasMore]);

	// Auto-load more badges to fill visible area when expanded
	useEffect(() => {
		if (isExpanded && hasMore && !isLoading && badgeList?.length > 0) {
			const container = containerRef.current;
			if (container) {
				const { scrollHeight, clientHeight } = container;
				if (scrollHeight <= clientHeight + 20) {
					fetchMoreData();
				}
			}
		}
	}, [badgeList?.length, hasMore, isLoading, fetchMoreData, isExpanded]);

	const handleBadgeClick = (badge) => {
		if (handleIconClickEvent) {
			handleIconClickEvent(badge);
		} else if (!isDetailsShown) {
			handleOpenViewReward(badge.id, null, badge);
		}
	};

	const renderBadge = (badge, index) => {
		const hasSnappyId = badge?.snappyGrantId || badge?.snappyGrant?.id;
		const isManagerOrAdmin = ['manager', 'admin', 'superadmin'].includes(
			currentUser?.role?.toLowerCase()
		);
		const hideSnappyIcon = !(hasSnappyId && isManagerOrAdmin);
		const currencySymbol = badge?.snappyGrant?.currencySymbol || '$';
		const isHidden = badge.visibility === false && !getMyBadge;

		const snappyDetails = hasSnappyId
			? {
					snappyGrantId: badge?.snappyGrantId || badge?.snappyGrant?.id,
					snappyGrantName: badge?.snappyGrant?.name || 'Snappy Reward',
					costInCurrency: `${currencySymbol}${badge?.snappyGrant?.costInCurrency || '0'}`,
					description:
						badge?.snappyGrant?.description || 'Snappy Gift Attached ⭐️',
				}
			: {};

		return (
			<motion.div
				key={badge.assignmentId || index}
				layout
				initial={{ opacity: 0, scale: 0.6, y: 40 }}
				animate={{ opacity: 1, scale: 1, y: 0 }}
				exit={{ opacity: 0, scale: 0.8, y: -20 }}
				whileHover={{ scale: 1.05, y: -5 }}
				whileTap={{ scale: 0.95 }}
				transition={{
					duration: 0.8,
					ease: 'easeOut',
					delay: index * 0.15,
					layout: { duration: 0.3 },
				}}
				style={{
					position: 'relative',
					cursor: 'pointer',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					padding: '12px',
					borderRadius: '12px',
					background: 'rgba(255, 255, 255, 0)',
					backdropFilter: 'blur(10px)',
					boxShadow: '0 2px 12px rgba(0, 0, 0, 0.01)',
					minWidth: '160px',
					maxWidth: '180px',
					height: 'fit-content',
					justifySelf: 'center',
				}}
				onClick={() => handleBadgeClick(badge)}
			>
				{isHidden && (
					<div
						style={{
							position: 'absolute',
							inset: 0,
							borderRadius: '12px',
							background: 'rgba(255, 255, 255, 0.6)',
							zIndex: 1,
							pointerEvents: 'none',
						}}
					/>
				)}
				<motion.div
					whileHover={{ rotate: [0, -5, 5, 0] }}
					transition={{ duration: 0.5 }}
				>
					<CoreBadge
						badgeType={badge?.badgeType}
						badgeIcon={badge?.iconInfo?.code}
						label={badge?.label}
						color={badge?.color}
						gradientColor={badge?.gradientColor}
						shape={badge?.shape}
						badgeRotation={badge?.badgeRotation}
						customImageSignedUrl={badge?.customImageSignedUrl}
						labelColor={badge?.labelColor}
						customIconSignedUrl={badge?.customIconSignedUrl}
						hideSnappyIcon={hideSnappyIcon}
						snappyDetails={snappyDetails}
					/>
				</motion.div>

				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 0.5 + index * 0.15 }}
					style={{
						textAlign: 'center',
						marginTop: '8px',
						fontSize: '14px',
						fontWeight: '500',
						color: '#666',
					}}
				>
					<span style={{ color: '#000c17ff', fontWeight: '600' }}>
						<span>{badge.name}</span>
					</span>
					{badge.claimTag && (
						<div style={{ marginTop: '6px' }}>
							<span
								style={{
									display: 'inline-block',
									padding: '2px 10px',
									borderRadius: '999px',
									background: '#f0f5ff',
									color: 'var(--theme-primary, #1890ff)',
									fontSize: '12px',
									fontWeight: 700,
								}}
							>
								{badge.claimTag}
							</span>
						</div>
					)}
				</motion.div>

				{isDetailsShown && (
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.7 + index * 0.15 }}
						style={{ marginTop: '8px' }}
					>
						<Button
							type="link"
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								handleOpenViewReward(
									badge.id,
									badge.completedAt || badge.issuedDate,
									badge
								);
							}}
							style={{
								fontSize: '11px',
								padding: '2px 8px',
								height: 'auto',
								color: '#1890ff',
							}}
						>
							Details
						</Button>
					</motion.div>
				)}
			</motion.div>
		);
	};

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.8 }}
			style={{
				background: 'rgba(255, 255, 255, 1)',
				borderRadius: '14px',
				padding: '16px',
				border: '1px solid #dddddd',
				height: noCollapse ? '100%' : '',
				marginBottom: noCollapse ? '0' : '30px',
			}}
		>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: noCollapse ? '0 0 14px 0' : '0 0 20px 0' }}>
				<motion.h4
					initial={{ opacity: 0, x: -20 }}
					animate={{ opacity: 1, x: 0 }}
					transition={{ delay: 0.2 }}
					style={{
						margin: 0,
						fontSize: noCollapse ? '14px' : '18px',
						fontWeight: '700',
						color: 'var(--dove-gray)',
						textAlign: 'left',
					}}
				>
					{title}
				</motion.h4>
				{titleLink && titleLinkText && (
					<Link to={titleLink} style={{ fontSize: '14px' }}>
						{titleLinkText}
					</Link>
				)}
			</div>

			<motion.div
				ref={containerRef}
				animate={{
					maxHeight: noCollapse
						? '448px'
						: isExpanded
							? customHeight || '500px'
							: '250px',
				}}
				transition={{
					duration: 0.8,
					ease: 'easeInOut',
				}}
				style={{
					overflowY: 'auto',
					overflowX: 'hidden',
					scrollbarWidth: 'thin',
					scrollbarColor: 'rgba(0,0,0,0.2) transparent',
				}}
			>
				{badgeList?.length > 0 ? (
					<motion.div
						layout
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
							gap: '32px',
							padding: '12px',
						}}
					>
						<AnimatePresence mode="popLayout">
							{badgeList.map((badge, index) => renderBadge(badge, index))}
						</AnimatePresence>
					</motion.div>
				) : !isLoading ? (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						style={{
							textAlign: 'center',
							padding: '40px',
							color: '#595959',
							fontSize: '14px',
						}}
					>
						No badges available
					</motion.div>
				) : null}

				<AnimatePresence>
					{hasMore && isLoading && (
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -20 }}
							style={{
								textAlign: 'center',
								padding: '20px',
							}}
						>
							<Spin size="small" />
						</motion.div>
					)}
				</AnimatePresence>
			</motion.div>

			{/* <AnimatePresence> */}
			{isLoading && !hasMore && (
				<div
					style={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%,-50%)',
						msTransform: 'translate(-50%,-50%)',
					}}
				>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						style={{
							textAlign: 'center',
							padding: '4px',
						}}
					>
						<Spin />
					</motion.div>
				</div>
			)}
			{/* </AnimatePresence> */}

			{!noCollapse && badgeList?.length > 5 && (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						marginTop: '16px',
					}}
				>
					<motion.button
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
						onClick={() => {
							setIsExpanded(!isExpanded);
							// Load more badges when expanding if we have more available
							if (!isExpanded && hasMore && !isLoading) {
								fetchMoreData();
							}
						}}
						style={{
							background: 'rgba(255, 255, 255, 0.14)',
							border: 'none',
							borderRadius: '20px',
							padding: '4px 8px',
							cursor: 'pointer',
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
							fontSize: '13px',
							fontWeight: '500',
							color: '#001278ff',
							backdropFilter: 'blur(10px)',
							boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
						}}
					>
						{isExpanded ? (
							<>
								Collapse <UpOutlined style={{ fontSize: '12px' }} />
							</>
						) : (
							<>
								Expand <DownOutlined style={{ fontSize: '12px' }} />
							</>
						)}
					</motion.button>
				</div>
			)}

			<ViewRewardModal
				isViewRewardModalVisible={isViewRewardModalVisible}
				handleCloseViewReward={handleCloseViewReward}
				rewardDetails={rewardDetails}
				isModalLoaderVisible={isModalLoaderVisible}
				isSharingVisible={false}
				currentUser={currentUser}
			/>
		</motion.div>
	);
};

export default ModernBadgeGrid;
